import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Compass,
  Users2,
  Rocket,
  LayoutDashboard,
  BookMarked,
  ArrowUpRight,
} from "lucide-react";
import { SOLUTIONS } from "../../lib/solutions";

const ICONS = { Compass, Users2, Rocket, LayoutDashboard, BookMarked };

export default function SolutionsSection() {
  return (
    <section
      data-testid="solutions-section"
      className="bg-[#F8F9FA] border-y-2 border-[#1A1A1A] relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-20 md:py-28">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-white border-2 border-[#1A1A1A] rounded-full px-3 py-1 nb-shadow-sm">
            <span className="h-2 w-2 rounded-full bg-[#FF007F]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase">
              Our Solutions
            </span>
          </div>
          <h2 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
            Practical solutions for{" "}
            <span className="bg-[#FFD700] px-2 inline-block">
              future-ready learning
            </span>
            .
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#4A4A4A] font-medium leading-relaxed max-w-3xl">
            From your first makerspace idea to a fully operational learning
            ecosystem, TinkerShift helps schools and libraries plan, implement
            and sustain creative, hands-on learning.
          </p>
          <p className="mt-3 text-sm md:text-base text-[#4A4A4A]/90 font-medium leading-relaxed max-w-3xl">
            Whether you’re starting from scratch, revitalising an existing
            space or building educator confidence, we provide practical
            solutions backed by more than a decade of TinkerTank experience.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {SOLUTIONS.map((s, i) => {
            const Icon = ICONS[s.icon] || Compass;
            return (
              <motion.article
                key={s.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  duration: 0.75,
                  delay: i * 0.07,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{ backgroundColor: s.color, color: s.text }}
                data-testid={`solution-card-${s.slug}`}
                className={`relative rounded-3xl nb-border-thick nb-shadow p-7 md:p-8 nb-lift overflow-hidden flex flex-col
                  ${i === 1 ? "lg:translate-y-8" : ""}
                  ${i === 3 ? "lg:-translate-y-4" : ""}
                  ${i === 4 ? "md:col-span-2 lg:col-span-1 lg:translate-y-6" : ""}
                `}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="h-12 w-12 rounded-xl bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] flex items-center justify-center nb-shadow-sm">
                    <Icon size={22} strokeWidth={2.5} />
                  </span>
                  <span className="font-display font-black text-5xl leading-none opacity-15">
                    {s.number}
                  </span>
                </div>
                <p className="mt-5 font-mono text-[11px] uppercase tracking-widest opacity-80">
                  {s.accent}
                </p>
                <h3 className="mt-1 font-display font-black text-2xl md:text-3xl leading-tight tracking-tighter">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm md:text-base font-semibold opacity-95 leading-relaxed">
                  {s.subheading}
                </p>
                <p className="mt-5 font-mono text-[11px] uppercase tracking-widest opacity-80">
                  From AUD ${s.investment.from.toLocaleString("en-AU")}
                  {s.investment.per ? ` / ${s.investment.per}` : ""}
                </p>
                <Link
                  to={`/solutions/${s.slug}`}
                  data-testid={`solution-card-cta-${s.slug}`}
                  className={`mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-bold uppercase text-xs tracking-widest border-2 nb-shadow-sm nb-press self-start ${
                    s.text === "#FFFFFF"
                      ? "bg-white text-[#1A1A1A] border-[#1A1A1A]"
                      : "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                  }`}
                >
                  {s.cta} <ArrowUpRight size={14} strokeWidth={2.5} />
                </Link>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/solutions"
            data-testid="solutions-explore-all"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-6 py-3 font-bold uppercase tracking-wide nb-shadow-sm nb-press"
          >
            Explore all solutions <ArrowUpRight size={16} strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}
